import Link from "next/link";
import { customEvent, Spacing } from "goi_common";

import { ArrowRightIcon } from "@/components/DirectionsIcons";
import { REGION_HERO_EYEBROW } from "@/constants/regionHero";
import { toManwonRange } from "@/utils/formatMoney";
import { RegionCrematorium } from "@/utils/regionFacilities";

interface RegionHeroProps {
  sidoName: string;
  sigunguName: string;
  /**
   * 이 지역에 장례식장이 소재하는지. false 면 parlorCount 가 인근 시설 수라
   * 시군구명에 그대로 붙이면 거짓이 된다
   */
  hasParlorInRegion?: boolean;
  parlorCount: number;
  publicCount: number;
  privateCount: number;
  binsoMinCharge: number | null;
  binsoMaxCharge: number | null;
  familyCostMin: number | null;
  familyCostMax: number | null;
  insideCrematorium: RegionCrematorium | null;
  totalCostHref: string;
  /** 시도 페이지 여부. breadcrumb 단계와 h1 문구가 달라진다 */
  isSido?: boolean;
  regionCount?: number;
  /** 주로 소개하는 대상. 카피와 첫 지표만 바뀐다 */
  focus?: "parlor" | "facility" | "crematorium";
  burialCount?: number;
  /** 목록에 담긴 화장장 수(인근 포함) */
  crematoriaCount?: number;
  /** 이 지역에 실제로 소재한 화장장 수. 제목의 개수는 이 값을 쓴다 */
  crematoriaInRegion?: number;
  facilityHref?: string;
}

/**
 * 히어로. 텍스트와 통계 카드만 둔다. 이미지는 두지 않는다.
 *
 * - h1과 통계 카드가 접히지 않아야 한다. 관내/관외 요금이 이 페이지의 핵심 숫자다
 * - 225개 페이지가 공용으로 쓰는 이미지 1장은 지역별 내용과 무관한 장식이 된다
 */
export const RegionHero = ({
  sidoName,
  sigunguName,
  parlorCount,
  publicCount,
  privateCount,
  binsoMinCharge,
  binsoMaxCharge,
  familyCostMin,
  familyCostMax,
  insideCrematorium,
  totalCostHref,
  isSido = false,
  hasParlorInRegion = true,
  regionCount = 0,
  focus = "parlor",
  crematoriaCount = 0,
  crematoriaInRegion = 0,
  burialCount = 0,
  facilityHref,
}: RegionHeroProps) => {
  const isFacilityFocus = focus === "facility";
  const isCrematoriumFocus = focus === "crematorium";
  /**
   * breadcrumb 이 가리킬 표면. parlor 와 facility 는 지금까지 둘 다 parlor
   * 경로를 썼고 그대로 둔다(기존 225 x 2 페이지의 출력이 바뀌면 회귀다).
   * 화장장만 자기 경로를 쓴다.
   */
  const crumbKind = isCrematoriumFocus ? "crematorium" : "parlor";
  const binsoRange = toManwonRange(binsoMinCharge, binsoMaxCharge);
  const familyRange = toManwonRange(familyCostMin, familyCostMax);

  return (
    <section className="rg-hero" aria-labelledby="rg-page-title">
      <div className="rg-hero__inner">
        <nav className="rg-crumb" aria-label="현재 위치">
          <Link
            href={isCrematoriumFocus ? "/region/crematorium/" : "/"}
            className="rg-crumb__link"
          >
            {isCrematoriumFocus ? "전국 화장장" : "장례식장"}
          </Link>
          <span aria-hidden="true">›</span>
          {isSido ? (
            <span className="rg-crumb__current" aria-current="page">
              {sidoName}
            </span>
          ) : (
            <>
              <Link
                href={`/region/${crumbKind}/${encodeURIComponent(sidoName)}/`}
                className="rg-crumb__link"
                onClick={() =>
                  customEvent({
                    newGtm: {
                      path: "prlr_region",
                      trigger_and_target: "crumb_sido",
                    },
                  })
                }
              >
                {sidoName}
              </Link>
              <span aria-hidden="true">›</span>
              <span className="rg-crumb__current" aria-current="page">
                {sigunguName}
              </span>
            </>
          )}
        </nav>

        <div className="rg-hero__text">
          <p className="rg-hero__eyebrow">
            {isCrematoriumFocus
              ? "화장장 관내, 관외 요금과 위치 한눈에 알아보기"
              : isFacilityFocus
              ? "장지 비용, 위치, 시설 정보 한눈에 알아보기"
              : REGION_HERO_EYEBROW}
          </p>

          <h1 id="rg-page-title" className="rg-hero__title">
            {isCrematoriumFocus
              ? // 시도는 그 시도 소재 시설 수를 쓴다. 목록 길이를 쓰면
                // "서울 화장장 19곳" 처럼 사실과 다른 문장이 된다(서울 소재 1곳).
                // 시군구는 소재 수가 0~1곳이라 개수로 세우면 페이지가 비어 보이므로
                // 목록 기준으로 쓰되 "가까운" 을 붙여 범위를 밝힌다
                isSido
                ? `${sigunguName} 화장장 ${crematoriaInRegion}곳,`
                : `${sigunguName} 화장장, 인근 포함 ${crematoriaCount}곳`
              : isFacilityFocus
              ? `${sigunguName} 장지 ${burialCount}곳,`
              : // 장례식장 0곳 시군구는 목록이 인근 시설이다. 화장장과 같은
              // "인근 포함" 표기로 개수의 범위를 밝힌다
              isSido || hasParlorInRegion
              ? `${sigunguName} 장례식장 ${parlorCount}곳,`
              : `${sigunguName} 장례식장, 인근 포함 ${parlorCount}곳`}
            <br />
            <span className="rg-accent">
              {isSido
                ? `${regionCount}개 지역별로 비교`
                : isCrematoriumFocus
                ? "관내 관외 요금 비교"
                : isFacilityFocus
                ? "인근 장례식장 및 화장장까지"
                : // 앞줄이 이미 "인근 포함 N곳" 이라 "인근" 을 겹쳐 쓰지 않는다
                hasParlorInRegion
                ? "인근 장지 및 화장장까지"
                : "장지 및 화장장까지"}
            </span>
          </h1>

          <p className="rg-hero__lede">
            {isCrematoriumFocus
              ? `${sigunguName}에서 이용할 수 있는 화장장(화장터, 승화원)을 가까운 순으로 안내합니다. 화장장은 전국 63곳뿐이라 목록에 다른 지역 시설도 포함됩니다. 거주지에 따라 갈리는 관내와 관외 요금, 감면 대상, 인근 장례식장까지 함께 확인하세요.`
              : isFacilityFocus
              ? `${sidoName} ${sigunguName}에 위치한 장지(봉안당, 자연장, 묘지)의 위치와 시설 정보를 확인해보세요. 인근 장례식장과 화장장까지 함께 볼 수 있습니다.`
              : isSido
              ? `${sidoName}에 위치한 장례식장을 지역별로 골라 확인해보세요. 빈소 사용료, 공설과 사설 구분, 주차 대수를 비교하고 인근 화장장 요금과 장지 분양가까지 볼 수 있습니다.`
              : hasParlorInRegion
              ? `${sidoName} ${sigunguName}에 위치한 장례식장(빈소 사용료, 공설과 사설 구분, 주차 대수 등)을 확인해보세요. 인근 화장장 요금과 장지 분양가까지 확인할 수 있습니다.`
              : `${sidoName} ${sigunguName}에는 장례식장이 없어 가까운 인근 시설을 안내합니다. 빈소 사용료, 공설과 사설 구분, 주차 대수를 비교하고 인근 화장장 요금과 장지 분양가까지 확인할 수 있습니다.`}
          </p>

          <a
            className="rg-hero__cta"
            href={
              isFacilityFocus ? facilityHref ?? totalCostHref : totalCostHref
            }
            onClick={() =>
              customEvent({
                newGtm: {
                  path: "prlr_region",
                  trigger_and_target: "hero_total_cost",
                },
              })
            }
          >
            <span>
              {isCrematoriumFocus
                ? "장례 전체 비용 알아보기"
                : isFacilityFocus
                ? "장지 정보, 비용 알아보기"
                : "장례식장 정보, 비용, 예약 알아보기"}
            </span>
            <ArrowRightIcon size={15} />
          </a>
        </div>

        <Spacing gap={24} />

        <dl className="rg-stats">
          <div className="rg-stat">
            <dt>
              {isCrematoriumFocus
                ? "화장장"
                : isFacilityFocus
                ? "장지"
                : "장례식장"}
            </dt>
            <dd className="rg-stat__value">
              {isCrematoriumFocus
                ? isSido
                  ? crematoriaInRegion
                  : crematoriaCount
                : isFacilityFocus
                ? burialCount
                : parlorCount}
              곳
            </dd>
            <p className="rg-stat__sub">
              {isCrematoriumFocus
                ? isSido
                  ? `인근 포함 ${crematoriaCount}곳 안내`
                  : `인근 포함, 장례식장 ${parlorCount}곳도 함께`
                : isFacilityFocus
                ? `장례식장 ${parlorCount}곳도 함께`
                : hasParlorInRegion
                ? `공설 ${publicCount}, 사설 ${privateCount}`
                : `인근 포함, 공설 ${publicCount} 사설 ${privateCount}`}
            </p>
          </div>

          {binsoRange && (
            <div className="rg-stat">
              <dt>빈소 사용료</dt>
              <dd className="rg-stat__value rg-accent">{binsoRange}</dd>
              <p className="rg-stat__sub">시설별 최저 요금 기준</p>
            </div>
          )}

          {/* 관내와 관외를 범위로 붙이면 이 지역의 화장 요금 격차가 히어로에서 바로 보인다 */}
          {insideCrematorium ? (
            <div className="rg-stat">
              <dt>화장 요금</dt>
              <dd className="rg-stat__value">
                {`${insideCrematorium.appliedPrice} ~ ${insideCrematorium.outsidePrice}`}
              </dd>
              <p className="rg-stat__sub">
                {`${insideCrematorium.companyName} 관내 ~ 관외`}
              </p>
            </div>
          ) : (
            familyRange && (
              <div className="rg-stat">
                <dt>가족장 예상 비용</dt>
                <dd className="rg-stat__value">{familyRange}</dd>
                <p className="rg-stat__sub">시설 비용 기준, 서비스 비용 별도</p>
              </div>
            )
          )}
        </dl>
      </div>
    </section>
  );
};
